<template>
  <div class="prep-budget-table">
    <div class="prep-budget-save">
      <b-button
        v-if="canImportBudget"
        class="prep-action-btn prep-action-btn-fill"
        variant="outline-primary"
        size="sm"
        :disabled="loading || saving || autosaving || importing"
        @click="openAddOnMenu"
      >
        Kitöltés
      </b-button>
      <b-button
        v-if="canImportBudget"
        class="prep-action-btn prep-action-btn-import"
        variant="outline-primary"
        size="sm"
        :disabled="loading || saving || autosaving || importing"
        @click="openExcelImportModal"
      >
        Importálás
      </b-button>
      <b-button
        v-if="canClearBudget"
        class="prep-action-btn prep-action-btn-clear"
        variant="outline-danger"
        size="sm"
        :disabled="loading || saving || autosaving || importing || clearing"
        @click="openClearModal"
      >
        Ürítés
      </b-button>
      <b-button
        v-if="canManageStructureItems"
        class="prep-action-btn prep-action-btn-assign"
        variant="outline-primary"
        size="sm"
        :disabled="loading || saving || autosaving"
        @click="openAssignModal"
      >
        Hozzá rendelés
      </b-button>
      <b-button
        v-if="canManageStructureItems"
        class="prep-action-btn prep-action-btn-new"
        variant="outline-primary"
        size="sm"
        :disabled="loading || saving || autosaving"
        @click="openCreateModal"
      >
        Új
      </b-button>
      <b-button
        class="prep-action-btn prep-action-btn-refresh"
        :variant="hasPersistedEntries ? 'primary' : 'outline-secondary'"
        size="sm"
        :disabled="loading || saving || !hasPersistedEntries || !canPersistEntries"
        @click="refreshItems"
      >
        Frissítés
      </b-button>
      <b-button
        class="prep-action-btn prep-action-btn-save"
        :variant="hasPersistedEntries ? 'outline-secondary' : 'primary'"
        size="sm"
        :disabled="saving || loading || hasPersistedEntries || !canPersistEntries"
        @click="saveItems"
      >
        {{ saving ? 'Mentés...' : 'Mentés' }}
      </b-button>
      <label
        v-if="isOpciosMunkakCategory"
        class="prep-opcios-summary-toggle"
      >
        <input
          type="checkbox"
          :checked="opciosIncludeInSummary"
          @change="toggleOpciosInSummary($event.target.checked)"
        >
        <span>Belefoglalás a fő összesítőbe</span>
      </label>
    </div>
    <ajanlat-ar-import-modal
      :munkafazis_id="resolvedCategoryKey()"
      :offer-id="offerId"
      :offer-type="offerType"
      :visible="projectImportModalOpen"
      @close="projectImportModalOpen = false"
      @imported="onProjectPricesImported"
    />
    <b-modal
      v-model="assignModalOpen"
      title="Tételek hozzárendelése"
      centered
      size="lg"
      @ok.prevent="confirmAssignSelection"
      @shown="focusAssignList"
    >
      <div
        v-if="assignLoading"
        class="prep-detail-placeholder"
      >
        Betöltés...
      </div>
      <div v-else>
        <b-form-input
          v-model="assignSearchQuery"
          class="mb-75"
          placeholder="Keresés a tételek között"
        />
        <div
          ref="assignList"
          class="prep-assign-list"
          tabindex="0"
          @keydown.enter.prevent="confirmAssignSelection"
        >
          <button
            v-for="row in filteredAssignCandidates"
            :key="`candidate-${row.id}`"
            type="button"
            class="prep-assign-item"
            :class="{ selected: selectedCandidateIds.includes(row.id) }"
            @click="onAssignCandidateClick(row, $event)"
          >
            <div>{{ row.item_text }}</div>
            <small
              v-if="row.source_category_title"
              class="text-muted d-block mt-25"
            >
              Forrás kategória: {{ row.source_category_title }}
            </small>
            <small
              v-if="row.subgroup_key"
              class="text-muted d-block mt-25"
            >
              Jelenlegi almenü: {{ row.subgroup_key }}
            </small>
          </button>
        </div>
        <div class="text-muted mt-50">
          Tipp: `Ctrl + kattintás` többszörös kijelöléshez, majd `Enter`.
        </div>
      </div>
    </b-modal>
    <b-modal
      v-model="createModalOpen"
      title="Új tétel"
      centered
      size="md"
      @ok.prevent="createNewItem"
    >
      <b-form-select
        v-model="newItemCategoryKey"
        class="mb-50"
        :options="newItemCategoryOptions"
        :disabled="newItemCategoryLoading"
      />
      <b-form-input
        v-model="newItemText"
        placeholder="Új tétel szövege"
      />
    </b-modal>
    <b-modal
      v-model="excelImportModalOpen"
      title="Excel import"
      centered
      size="md"
      @ok.prevent="confirmExcelImport"
      @hidden="resetExcelImportDraft"
    >
      <b-form-file
        v-model="excelImportFile"
        accept=".xlsx,.xls"
      />
      <small class="text-muted d-block mt-50">Tallózd be az Excel fájlt, majd OK.</small>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="importing"
            @click="cancel()"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="importing || !excelImportFile"
            @click="ok()"
          >
            {{ importing ? 'Importálás...' : 'OK' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="excelPreviewModalOpen"
      title="Import előnézet és jóváhagyás"
      centered
      size="lg"
      @ok.prevent="confirmExcelPreview"
    >
      <div
        v-if="!previewTargets.length"
        class="text-muted"
      >
        Nincs előnézeti adat.
      </div>
      <div
        v-else
        class="d-flex flex-column gap-75"
      >
        <div
          v-for="(target, idx) in previewTargets"
          :key="`preview-target-${idx}`"
          class="prep-assign-item"
        >
          <div><strong>Menü:</strong> {{ target.menu || '-' }}</div>
          <div><strong>Lap:</strong> {{ target.sheet || '-' }}</div>
          <div><strong>Státusz:</strong> {{ target.status || '-' }}</div>

          <div
            v-if="target.status !== 'missing_sheet'"
            class="mt-50 d-flex flex-column gap-50"
          >
            <div
              v-for="field in requiredFieldsForTarget(target)"
              :key="`preview-${idx}-${field}`"
            >
              <label class="d-block mb-25">
                {{ previewFieldLabel(field) }}
              </label>
              <b-form-select
                v-model="excelPreviewSelections[previewTargetKey(target, idx)][field]"
                :options="previewFieldOptions(target, field)"
              />
            </div>
          </div>

          <div
            v-if="target.status === 'missing_sheet'"
            class="mt-50 text-warning"
          >
            Ehhez a menühöz nem talált lapot az Excelben.
          </div>
          <div
            v-if="target.status === 'ready'"
            class="mt-50"
          >
            <div class="text-muted mb-25">
              Felismert oszlopok:
            </div>
            <div
              v-for="(resolved, rIdx) in previewResolvedRows(target)"
              :key="`ready-${idx}-${rIdx}`"
              class="text-muted"
            >
              {{ resolved.label }}: <strong>{{ resolved.column }}</strong>
            </div>
          </div>
        </div>
      </div>
      <small class="text-muted d-block mt-50">
        Az OK gomb a jóváhagyott oszlopokkal indítja a tényleges importálást.
      </small>
      <div
        v-if="previewSubmitBlockers.length"
        class="mt-50 text-warning"
      >
        <div
          v-for="(blocker, idx) in previewSubmitBlockers"
          :key="`preview-blocker-${idx}`"
        >
          {{ blocker }}
        </div>
      </div>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="importing"
            @click="cancel()"
          >
            Mégse
          </b-button>
          <b-button
            variant="primary"
            :disabled="importing || !canSubmitPreview"
            @click="ok()"
          >
            {{ importing ? 'Importálás...' : 'Jóváhagyás és import' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="excelResolutionModalOpen"
      title="Import oszlop kiválasztás"
      centered
      size="md"
      @ok.prevent="confirmExcelResolution"
    >
      <div
        v-if="excelImportResolution"
        class="mb-50"
      >
        <div><strong>Menü:</strong> {{ excelImportResolution.menu || '-' }}</div>
        <div><strong>Lap:</strong> {{ excelImportResolution.sheet || '-' }}</div>
        <div class="mt-50">
          {{ excelImportResolution.message || 'Válaszd ki a megfelelő oszlopot.' }}
        </div>
      </div>
      <div
        v-if="resolutionUnresolvedFields.length"
        class="d-flex flex-column gap-50"
      >
        <div
          v-for="field in resolutionUnresolvedFields"
          :key="`resolve-${field.field}`"
        >
          <label class="d-block mb-25">
            {{ field.field_label || field.field }}
          </label>
          <b-form-select
            v-model="excelResolutionSelections[field.field]"
            :options="fieldOptions(field)"
          />
        </div>
      </div>
      <small class="text-muted d-block mt-50">
        Az OK gombbal újrafut az import a kiválasztott oszloppal.
      </small>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="importing"
            @click="cancel()"
          >
            Mégse
          </b-button>
          <b-button
            variant="primary"
            :disabled="importing || !canSubmitResolution"
            @click="ok()"
          >
            {{ importing ? 'Importálás...' : 'OK' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="clearModalOpen"
      title="Kártya adatok ürítése"
      centered
      size="md"
      @ok.prevent="confirmClearOfferData"
      @hidden="resetClearDraft"
    >
      <div class="mb-75">
        Mit töröljünk az aktuális kártyáról?
      </div>
      <b-form-checkbox
        v-model="clearEntries"
      >
        Mentett költségvetési adatok (budget_offer_entries)
      </b-form-checkbox>
      <b-form-checkbox
        v-model="clearMenu"
        class="mt-50"
      >
        Menü felépítés (kártya menübeállítás)
      </b-form-checkbox>
      <small class="text-muted d-block mt-75">
        A törlés csak az aktuális kártyára vonatkozik.
      </small>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="clearing"
            @click="cancel()"
          >
            Cancel
          </b-button>
          <b-button
            variant="danger"
            :disabled="clearing || (!clearEntries && !clearMenu)"
            @click="ok()"
          >
            {{ clearing ? 'Törlés...' : 'Törlés' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="importReportModalOpen"
      title="Import validációs riport"
      centered
      size="lg"
      ok-only
      ok-title="Bezár"
    >
      <div v-if="importReport">
        <div
          v-if="importReportError"
          class="mb-75 text-danger"
        >
          {{ importReportError }}
        </div>
        <div class="mb-75">
          Feldolgozott lapok: <strong>{{ importReportSummary.processed_sheets }}</strong>,
          új tételek: <strong>{{ importReportSummary.new_items }}</strong>,
          mentett sorok: <strong>{{ importReportSummary.saved_rows }}</strong>
        </div>
        <div class="mb-75 text-muted">
          Egyezőség: rendben <strong>{{ importReportSummary.consistency.ok }}</strong>,
          eltérés <strong>{{ importReportSummary.consistency.mismatch }}</strong>,
          kihagyott <strong>{{ importReportSummary.consistency.skipped }}</strong>
        </div>
        <div
          v-if="Array.isArray(importReport.warnings) && importReport.warnings.length"
          class="mb-75 text-warning"
        >
          <div
            v-for="(warning, idx) in importReport.warnings"
            :key="`warn-${idx}`"
          >
            - {{ warning }}
          </div>
        </div>
        <div class="prep-assign-list">
          <div
            v-for="(row, idx) in importReportRows"
            :key="`report-${idx}`"
            class="prep-assign-item"
          >
            <strong>{{ row.menu }}</strong> |
            kategória: {{ row.category }} |
            lap: {{ row.sheet || '-' }} |
            státusz: {{ row.status }} |
            új tétel: {{ row.new_items || 0 }} |
            mentett sor: {{ row.saved_rows || 0 }} |
            egyezőség: {{ row.consistency_status || '-' }}
            <template v-if="row.consistency_status === 'mismatch'">
              | anyag diff: {{ formatFt(row.material_diff || 0) }}
              | díj diff: {{ formatFt(row.fee_diff || 0) }}
            </template>
          </div>
        </div>
      </div>
      <div v-else>
        Nincs riport adat.
      </div>
    </b-modal>
    <div class="prep-budget-table-scroll">
      <div
        v-if="loading"
        class="prep-detail-placeholder"
      >
        Betöltés...
      </div>
      <div
        v-else-if="error"
        class="prep-detail-placeholder"
      >
        {{ error }}
      </div>
      <draggable
        v-else
        v-model="draggableRows"
        tag="div"
        class="prep-budget-item-list"
        handle=".prep-drag-handle"
        :animation="160"
        :disabled="!canReorderRows"
        ghost-class="prep-budget-item-drag-ghost"
        chosen-class="prep-budget-item-drag-chosen"
        drag-class="prep-budget-item-dragging"
        @end="onRowsReordered"
      >
        <budget-item-card
          v-for="item in rowsForDisplay"
          :key="item.id"
          :item-id="item.id"
          :item-text="item.itemText"
          :value="item"
          :summary-mode="isSummaryCategory"
          :hide-material-columns="false"
          :show-planned-expense-control="!isSummaryCategory && !isPrepBudgetFillMode"
          :edit-mode="resolvedItemEditMode"
          :fill-mode="isPrepBudgetFillMode"
          :show-drag-handle="canReorderRows"
          :show-note-action="!isSummaryCategory"
          :show-actions="canManageStructureItems && (!usesTemplateBindings || isBaseBudgetOffer)"
          :show-remove-action="canRemoveBudgetRow"
          @change="updateRow"
          @edit-item-text="onEditItemText"
          @unlink-subgroup="onUnlinkSubgroup"
          @remove-item="onRemoveBudgetRow"
        />
        <div
          v-if="!rowsForDisplay.length"
          class="prep-detail-placeholder"
        >
          Nincs megjeleníthető tétel.
        </div>
      </draggable>
    </div>
    <div
      v-if="rowsForDisplay.length"
      class="prep-budget-summary-bar"
    >
      <div class="prep-budget-summary-title">
        Munkanem összesen NETTÓ ár:
      </div>
      <div class="prep-budget-summary-items">
        <div class="prep-budget-summary-item">
          <div class="prep-budget-summary-item-label-box">
            <span class="prep-budget-summary-item-label">Anyag összesen</span>
          </div>
          <div class="prep-budget-summary-item-value-box">
            <strong class="prep-budget-summary-item-value">{{ formatFt(totalMaterial) }}</strong>
          </div>
        </div>
        <div class="prep-budget-summary-item">
          <div class="prep-budget-summary-item-label-box">
            <span class="prep-budget-summary-item-label">Díj összesen</span>
          </div>
          <div class="prep-budget-summary-item-value-box">
            <strong class="prep-budget-summary-item-value">{{ formatFt(totalFee) }}</strong>
          </div>
        </div>
        <div class="prep-budget-summary-item total">
          <div class="prep-budget-summary-item-label-box">
            <span class="prep-budget-summary-item-label">Nettó összesen</span>
          </div>
          <div class="prep-budget-summary-item-value-box">
            <strong class="prep-budget-summary-item-value">{{ formatFt(totalNet) }}</strong>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BButton, BModal, BFormInput, BFormFile, BFormCheckbox, BFormSelect,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BudgetItemCard from './BudgetItemCard.vue'
import AjanlatArImportModal from '../../components/AjanlatArImportModal.vue'

export default {
  name: 'BudgetDeepFoundationList',
  components: {
    BudgetItemCard,
    AjanlatArImportModal,
    BButton,
    BModal,
    BFormInput,
    BFormFile,
    BFormCheckbox,
    BFormSelect,
    draggable,
  },
  props: {
    endpoint: {
      type: String,
      default: 'budget/items/mely-alapozas',
    },
    menuKey: {
      type: String,
      default: '',
    },
    categoryKey: {
      type: String,
      default: '',
    },
    subgroupKey: {
      type: String,
      default: '',
    },
    subgroupOptions: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: String,
      default: 'full',
    },
    showPositiveQtyOnly: {
      type: Boolean,
      default: false,
    },
    offerType: {
      type: String,
      default: '',
    },
    offerId: {
      type: [Number, String],
      default: null,
    },
    isBaseBudgetOffer: {
      type: Boolean,
      default: false,
    },
    allowRootActions: {
      type: Boolean,
      default: false,
    },
    rootOnlyItems: {
      type: Boolean,
      default: false,
    },
    menuType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      rows: [],
      loading: false,
      error: '',
      saving: false,
      autosaving: false,
      hasChanges: false,
      importing: false,
      clearing: false,
      excelImportModalOpen: false,
      excelImportFile: null,
      excelImportPreview: null,
      excelPreviewModalOpen: false,
      excelPreviewSelections: {},
      excelImportResolution: null,
      excelResolutionModalOpen: false,
      excelResolutionSelections: {},
      excelImportColumnOverrides: {},
      importReportModalOpen: false,
      importReport: null,
      importReportError: '',
      clearModalOpen: false,
      clearEntries: true,
      clearMenu: false,
      hasPersistedEntries: false,
      autosaveTimer: null,
      pendingAutosaveContext: null,
      pendingAutosaveEntries: null,
      correctionBaseRows: null,
      assignModalOpen: false,
      assignLoading: false,
      assignCandidates: [],
      assignSearchQuery: '',
      selectedCandidateIds: [],
      createModalOpen: false,
      newItemText: '',
      newItemCategoryKey: '',
      newItemCategoryOptions: [],
      newItemCategoryLoading: false,
      projectImportModalOpen: false,
    }
  },
  computed: {
    isOpciosMunkakCategory() {
      return String(this.categoryKey || '').trim() === 'opcios-munkak'
    },
    opciosIncludeInSummary() {
      if (!this.$store) return false
      const menus = (this.$store.state && this.$store.state.app && this.$store.state.app.prepBudgetCustomMenus) || []
      const entry = menus.find(m => String(m && m.key ? m.key : '') === 'opcios-munkak')
      return !!(entry && entry.include_in_summary)
    },
    canImportBudget() {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const roles = Array.isArray(user?.role) ? user.role : []
      return roles.includes('developer_role')
        || roles.includes('prep_view_all')
        || roles.includes('prep_assign_project')
        || roles.includes('prep_view_own')
    },
    canClearBudget() {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const roles = Array.isArray(user?.role) ? user.role : []
      return roles.includes('developer_role')
        || roles.includes('prep_view_all')
        || roles.includes('prep_assign_project')
        || roles.includes('prep_view_own')
    },
    isSummaryCategory() {
      return this.resolvedCategoryKey() === 'osszesito'
    },
    isSubgroupCategory() {
      return !!(this.subgroupKey && !this.isSummaryCategory)
    },
    usesTemplateBindings() {
      return ['uj', 'subgroup'].includes(String(this.menuType || '').trim())
    },
    isTemplateBindingEditor() {
      return this.usesTemplateBindings && this.isBaseBudgetOffer
    },
    isPrepBudgetFillMode() {
      return !!(this.$store
        && this.$store.state
        && this.$store.state.app
        && this.$store.state.app.prepBudgetFillMode)
    },
    resolvedItemEditMode() {
      if (this.isTemplateBindingEditor) return 'readonly'
      return this.editMode
    },
    canManageStructureItems() {
      if (this.isSummaryCategory) return false
      return this.isSubgroupCategory || this.allowRootActions
    },
    canRemoveBudgetRow() {
      return !this.isSummaryCategory
        && this.canPersistEntries
        && !this.isBaseBudgetOffer
    },
    filteredAssignCandidates() {
      const query = String(this.assignSearchQuery || '').trim().toLowerCase()
      if (!query) return this.assignCandidates

      return this.assignCandidates.filter(row => {
        const itemText = String(row.item_text || '').toLowerCase()
        const categoryTitle = String(row.source_category_title || '').toLowerCase()
        const subgroupKey = String(row.subgroup_key || '').toLowerCase()
        return itemText.includes(query) || categoryTitle.includes(query) || subgroupKey.includes(query)
      })
    },
    rowsForDisplay() {
      const { rows } = this
      if (!this.showPositiveQtyOnly) return rows
      return rows.filter(row => (Number(row.qty) || 0) > 0)
    },
    draggableRows: {
      get() {
        return this.rowsForDisplay
      },
      set(nextRows) {
        if (!Array.isArray(nextRows)) return
        if (!this.showPositiveQtyOnly) {
          this.rows = nextRows
          return
        }

        const reorderedRows = [...nextRows]
        this.rows = this.rows.map(row => ((Number(row.qty) || 0) > 0 ? reorderedRows.shift() || row : row))
      },
    },
    canReorderRows() {
      return this.rowsForDisplay.length > 1
        && this.canPersistEntries
        && !this.loading
        && !this.saving
        && !this.autosaving
        && !this.isSummaryCategory
    },
    canPersistEntries() {
      return !this.isTemplateBindingEditor
    },
    totalMaterial() {
      return this.rowsForDisplay.reduce((sum, row) => sum + (Number(row.material_total) || 0), 0)
    },
    totalFee() {
      return this.rowsForDisplay.reduce((sum, row) => sum + (Number(row.fee_total) || 0), 0)
    },
    totalNet() {
      return this.totalMaterial + this.totalFee
    },
    resolutionFieldOptions() {
      const columns = Array.isArray(this.excelImportResolution?.columns) ? this.excelImportResolution.columns : []
      return columns.map(col => ({
        value: Number(col.index),
        text: `${col.letter || ''} - ${col.label || ''}`,
      }))
    },
    resolutionUnresolvedFields() {
      const fields = this.excelImportResolution?.unresolved_fields
      if (Array.isArray(fields) && fields.length) return this.sortImportFields(fields)
      const field = this.excelImportResolution?.field
      if (!field) return []
      return this.sortImportFields([{
        field,
        field_label: this.excelImportResolution?.field_label || field,
        candidates: Array.isArray(this.excelImportResolution?.candidates) ? this.excelImportResolution.candidates : [],
      }])
    },
    previewTargets() {
      const targets = this.excelImportPreview?.targets
      return Array.isArray(targets) ? targets : []
    },
    canSubmitPreview() {
      if (!this.previewTargets.length) return false
      return this.previewTargets.some(target => target?.status !== 'missing_sheet')
    },
    previewSubmitBlockers() {
      const blockers = []
      this.previewTargets.forEach((target, idx) => {
        if (target?.status === 'missing_sheet') return

        const sheetLabel = String(target?.sheet || target?.menu || `#${idx + 1}`)
        const required = this.requiredFieldsForTarget(target)
        if (!required.length) return

        const selectedMap = this.excelPreviewSelections[this.previewTargetKey(target, idx)] || {}
        required.forEach(field => {
          const value = Number(selectedMap[field] || 0)
          if (!Number.isFinite(value) || value <= 0) {
            blockers.push(`${sheetLabel}: hiányzik a(z) ${this.previewFieldLabel(field)} oszlop kiválasztása.`)
          }
        })
      })
      return blockers
    },
    canSubmitResolution() {
      if (!this.resolutionUnresolvedFields.length) return false
      return this.resolutionUnresolvedFields.every(field => {
        const value = Number(this.excelResolutionSelections[field.field] || 0)
        return Number.isFinite(value) && value > 0
      })
    },
    importReportRows() {
      const rows = this.importReport?.targets
      return Array.isArray(rows) ? rows : []
    },
    importReportSummary() {
      const summary = this.importReport && this.importReport.summary ? this.importReport.summary : {}
      return {
        processed_sheets: Number(summary.processed_sheets || 0),
        new_items: Number(summary.new_items || 0),
        saved_rows: Number(summary.saved_rows || 0),
        consistency: {
          ok: Number(summary?.consistency?.ok || 0),
          mismatch: Number(summary?.consistency?.mismatch || 0),
          skipped: Number(summary?.consistency?.skipped || 0),
        },
      }
    },
  },
  watch: {
    endpoint() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    categoryKey() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    subgroupKey() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    offerType() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    offerId() {
      this.flushAutoPersist()
      this.fetchItems()
    },
  },
  mounted() {
    this.fetchItems()
  },
  beforeDestroy() {
    this.flushAutoPersist()
  },
  methods: {
    openAddOnMenu() {
      if (!this.$store) return
      const current = !!(this.$store.state && this.$store.state.app && this.$store.state.app.prepBudgetFillMode)
      this.$store.commit('app/SET_PREP_BUDGET_FILL_MODE', !current)
    },
    toggleOpciosInSummary(val) {
      if (!this.$store) return
      this.$store.commit('app/UPDATE_PREP_BUDGET_CUSTOM_MENU', {
        key: 'opcios-munkak',
        updates: { include_in_summary: !!val },
      })
    },
    openAssignModal() {
      const category = this.resolvedCategoryKey()
      if (!category) return
      this.assignModalOpen = true
      this.assignLoading = true
      this.assignSearchQuery = ''
      this.selectedCandidateIds = []
      const token = localStorage.getItem('token')
      const loadCandidates = async () => {
        if (this.usesTemplateBindings || !this.isBaseBudgetOffer) {
          const response = await axios({
            method: 'get',
            url: 'budget/subgroup-mapping/base-items',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          return Array.isArray(response.data?.rows) ? response.data.rows : []
        }

        const response = await axios({
          method: 'get',
          url: 'budget/subgroup-mapping/items',
          params: {
            category,
            unassigned_only: 0,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        return Array.isArray(response.data?.rows) ? response.data.rows : []
      }

      loadCandidates().then(rows => {
        this.assignCandidates = rows.map(row => ({
          id: String(row.id),
          item_text: row.item_text || '',
          source_category: row.source_category || '',
          source_category_title: row.source_category_title || '',
          item_id: row.item_id ?? row.id,
          subgroup_key: row.subgroup_key || '',
        }))
      }).catch(async () => {
        if (!this.isBaseBudgetOffer) {
          this.assignCandidates = []
          this.notify('Alap költségvetés tételek betöltése sikertelen', 'danger', 'XOctagonIcon')
          return
        }
        try {
          const fallback = await axios({
            method: 'get',
            url: `budget/items/category/${encodeURIComponent(category)}`,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          const rows = Array.isArray(fallback.data) ? fallback.data : []
          this.assignCandidates = rows.map(row => ({
            id: String(row.id),
            item_text: row.item_text || '',
            source_category: '',
            source_category_title: '',
            item_id: row.id,
            subgroup_key: '',
          }))
        } catch (error) {
          this.assignCandidates = []
          this.notify('Hozzárendelhető tételek betöltése sikertelen', 'danger', 'XOctagonIcon')
        }
      }).finally(() => {
        this.assignLoading = false
      })
    },
    focusAssignList() {
      this.$nextTick(() => {
        const el = this.$refs.assignList
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    onAssignCandidateClick(row, event) {
      const itemId = String(row.id)
      const isMulti = !!(event && (event.ctrlKey || event.metaKey))
      if (isMulti) {
        if (this.selectedCandidateIds.includes(itemId)) {
          this.selectedCandidateIds = this.selectedCandidateIds.filter(id => id !== itemId)
        } else {
          this.selectedCandidateIds = [...this.selectedCandidateIds, itemId]
        }
        return
      }
      this.assignItems([itemId])
    },
    confirmAssignSelection() {
      if (!this.selectedCandidateIds.length) return
      this.assignItems(this.selectedCandidateIds)
    },
    assignRowsInSelectionOrder(itemIds) {
      const rowsById = new Map(
        this.assignCandidates.map(row => [String(row.id), row]),
      )
      return itemIds
        .map(id => rowsById.get(String(id)))
        .filter(Boolean)
    },
    assignItems(itemIds) {
      const category = this.resolvedCategoryKey()
      if (!category || !itemIds.length) return

      if (this.usesTemplateBindings) {
        const sourceItems = this.assignRowsInSelectionOrder(itemIds)
          .map(row => ({
            source_category: String(row.source_category || ''),
            item_id: Number(row.item_id || 0),
          }))
          .filter(row => row.source_category && row.item_id > 0)

        if (!sourceItems.length) return

        axios({
          method: 'post',
          url: 'budget/template-bindings/save',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            main_menu_key: this.templateBindingKey(),
            sub_menu_key: null,
            source_items: sourceItems,
          },
        }).then(() => {
          this.notify('Kártyafüggetlen tétel-kötés mentve', 'success', 'CheckIcon')
          this.assignModalOpen = false
          this.selectedCandidateIds = []
          this.fetchItems(false)
        }).catch(() => {
          this.notify('A tétel-kötés mentése sikertelen', 'danger', 'XOctagonIcon')
        })
        return
      }

      if (!this.isBaseBudgetOffer) {
        const sourceItems = this.assignRowsInSelectionOrder(itemIds)
          .map(row => ({
            source_category: String(row.source_category || ''),
            item_id: Number(row.item_id || 0),
          }))
          .filter(row => row.source_category && row.item_id > 0)

        if (!sourceItems.length) return

        axios({
          method: 'post',
          url: 'budget/subgroup-mapping/import-from-base',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            target_category: category,
            subgroup_key: this.subgroupKey || null,
            source_items: sourceItems,
          },
        }).then(() => {
          this.notify('Tételek beemelve az alap költségvetésből', 'success', 'CheckIcon')
          this.assignModalOpen = false
          this.selectedCandidateIds = []
          this.fetchItems(false)
        }).catch(() => {
          this.notify('Alap költségvetés tételek beemelése sikertelen', 'danger', 'XOctagonIcon')
        })
        return
      }

      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/assign',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          subgroup_key: this.subgroupKey || null,
          item_ids: itemIds,
        },
      }).then(() => {
        this.notify('Tételek hozzárendelve', 'success', 'CheckIcon')
        this.assignModalOpen = false
        this.selectedCandidateIds = []
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Tétel hozzárendelés sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    openCreateModal() {
      this.newItemText = ''
      this.newItemCategoryKey = this.resolvedCategoryKey()
      this.createModalOpen = true
      this.loadNewItemCategoryOptions()
    },
    loadNewItemCategoryOptions() {
      this.newItemCategoryLoading = true
      axios({
        method: 'get',
        url: 'budget/source-categories',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const rows = Array.isArray(response.data?.rows) ? response.data.rows : []
        this.newItemCategoryOptions = rows
          .map(row => ({
            value: String(row.category || ''),
            text: String(row.title || row.category || ''),
          }))
          .filter(row => row.value && row.text)
        const selectedExists = this.newItemCategoryOptions.some(row => row.value === this.newItemCategoryKey)
        if (!selectedExists && this.newItemCategoryOptions.length) {
          this.newItemCategoryKey = this.newItemCategoryOptions[0].value
        }
      }).catch(() => {
        this.newItemCategoryOptions = []
      }).finally(() => {
        this.newItemCategoryLoading = false
      })
    },
    createNewItem() {
      const category = String(this.newItemCategoryKey || this.resolvedCategoryKey() || '').trim()
      const text = String(this.newItemText || '').trim()
      if (!category || !text) return

      if (this.usesTemplateBindings) {
        axios({
          method: 'post',
          url: 'budget/subgroup-mapping/create-item',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            category,
            subgroup_key: null,
            item_text: text,
          },
        }).then(response => {
          const itemId = Number(response?.data?.id || 0)
          if (!itemId) {
            throw new Error('missing_created_item_id')
          }

          return axios({
            method: 'post',
            url: 'budget/template-bindings/save',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            data: {
              main_menu_key: this.templateBindingKey(),
              sub_menu_key: null,
              source_items: [{
                source_category: category,
                item_id: itemId,
              }],
            },
          })
        }).then(() => {
          this.notify('Új alaptétel létrehozva és hozzárendelve', 'success', 'CheckIcon')
          this.createModalOpen = false
          this.fetchItems(false)
        }).catch(() => {
          this.notify('Új alaptétel létrehozása sikertelen', 'danger', 'XOctagonIcon')
        })
        return
      }

      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/create-item',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          subgroup_key: this.subgroupKey || null,
          item_text: text,
        },
      }).then(() => {
        this.notify('Új tétel létrehozva', 'success', 'CheckIcon')
        this.createModalOpen = false
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Új tétel létrehozása sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    onEditItemText(payload) {
      const itemId = Number(payload && payload.item_id)
      const currentText = String((payload && payload.item_text) || '')
      if (!itemId) return
      const nextText = window.prompt('Tétel szövege:', currentText)
      if (nextText === null) return
      const clean = String(nextText).trim()
      if (!clean) return

      let category = this.resolvedCategoryKey()
      let targetItemId = itemId
      if (this.usesTemplateBindings) {
        if (!this.isBaseBudgetOffer) {
          this.notify('A forrástétel szövegét az Alapköltség vetés kártyán szerkeszd.', 'warning', 'AlertCircleIcon')
          return
        }
        const row = this.rows.find(item => Number(item.id) === itemId)
        category = String((row && row.source_category) || '')
        targetItemId = Number((row && row.source_item_id) || 0)
      }
      if (!category || !targetItemId) return

      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/update-item-text',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          item_id: targetItemId,
          item_text: clean,
        },
      }).then(() => {
        this.notify('Tétel szöveg frissítve', 'success', 'CheckIcon')
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Tétel szöveg frissítése sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    onUnlinkSubgroup(payload) {
      const itemId = Number(payload && payload.item_id)
      if (!itemId) return

      if (this.usesTemplateBindings) {
        if (!this.isBaseBudgetOffer) {
          this.notify('A globális tétel-kötést az Alapköltség vetés kártyán töröld.', 'warning', 'AlertCircleIcon')
          return
        }
        axios({
          method: 'post',
          url: 'budget/template-bindings/delete',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            binding_id: itemId,
          },
        }).then(() => {
          this.notify('Tétel-kötés törölve', 'success', 'CheckIcon')
          this.fetchItems(false)
        }).catch(() => {
          this.notify('Tétel-kötés törlése sikertelen', 'danger', 'XOctagonIcon')
        })
        return
      }

      const category = this.resolvedCategoryKey()
      if (!category) return
      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/unlink-item',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          item_id: itemId,
        },
      }).then(() => {
        this.notify('Almenü kapcsolat törölve', 'success', 'CheckIcon')
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Almenü kapcsolat törlése sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    onRemoveBudgetRow(payload) {
      const itemId = Number(payload && payload.item_id)
      if (!itemId || !this.canRemoveBudgetRow) return
      const rowIndex = this.rows.findIndex(item => Number(item.id) === itemId)
      if (rowIndex < 0) return

      this.rows.splice(rowIndex, 1)
      this.correctionBaseRows = null
      this.hasChanges = true
      this.scheduleAutoPersist()
      this.notify('TĂ©tel tĂ¶rĂ¶lve a kĂˇrtyĂˇbĂłl', 'success', 'CheckIcon')
    },
    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    formatFt(value) {
      const number = Math.round(Number(value || 0))
      return `${number.toLocaleString('hu-HU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })} Ft`
    },
    notify(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },
    openExcelImportModal() {
      if (!this.canImportBudget) return
      this.excelImportModalOpen = true
    },
    openProjectImportModal() {
      if (!this.canImportBudget || !this.canPersistEntries || this.isSummaryCategory) return
      this.projectImportModalOpen = true
    },
    onProjectPricesImported(frissitett) {
      this.projectImportModalOpen = false
      this.fetchItems(false)
      this.notify(`${frissitett} tĂ©tel Ăˇra frissĂ­tve`, 'success', 'CheckIcon')
    },
    resetExcelImportDraft() {
      if (this.excelResolutionModalOpen || this.excelPreviewModalOpen) return
      this.excelImportFile = null
      this.importing = false
      this.excelImportPreview = null
      this.excelPreviewSelections = {}
      this.excelImportResolution = null
      this.excelResolutionSelections = {}
      this.excelImportColumnOverrides = {}
      this.importReportError = ''
    },
    confirmExcelImport() {
      this.runExcelImport({ previewOnly: true })
    },
    runExcelImport({ previewOnly = false } = {}) {
      if (!this.canImportBudget) return
      const file = this.excelImportFile
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)
      if (!file || !offerType || !offerId) return

      const formData = new FormData()
      formData.append('offer_type', offerType)
      formData.append('offer_id', String(offerId))
      formData.append('file', file)
      formData.append('preview_only', previewOnly ? '1' : '0')
      formData.append('column_overrides', JSON.stringify(this.excelImportColumnOverrides || {}))

      this.importing = true
      axios({
        method: 'post',
        url: 'budget/offer/import-xlsx',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }).then(response => {
        if (previewOnly) {
          this.excelImportPreview = response?.data?.preview || null
          this.hydratePreviewSelections()
          this.excelImportModalOpen = false
          this.excelPreviewModalOpen = true
          this.notify('Import előnézet betöltve', 'success', 'CheckIcon')
          return
        }
        this.notify('Excel import sikeres', 'success', 'CheckIcon')
        this.excelImportModalOpen = false
        this.excelPreviewModalOpen = false
        this.excelResolutionModalOpen = false
        this.excelImportPreview = null
        this.excelPreviewSelections = {}
        this.excelImportResolution = null
        this.excelResolutionSelections = {}
        this.excelImportColumnOverrides = {}
        this.importReportError = ''
        this.importReport = response?.data?.report || null
        this.importReportModalOpen = !!this.importReport
        this.$emit('budget-imported', this.importReport)
        this.fetchItems(false)
      }).catch(error => {
        if (error?.response?.status === 409 && error?.response?.data?.resolution) {
          const { resolution } = error.response.data
          this.excelImportResolution = resolution
          this.importReport = error?.response?.data?.report || this.importReport
          const defaults = {}
          this.resolutionUnresolvedFields.forEach(field => {
            let firstCandidate = null
            if (Array.isArray(field?.candidates) && field.candidates.length) {
              firstCandidate = Number(field.candidates[0])
            } else if (this.resolutionFieldOptions.length) {
              firstCandidate = Number(this.resolutionFieldOptions[0].value)
            }
            if (Number.isFinite(firstCandidate) && firstCandidate > 0) {
              defaults[field.field] = firstCandidate
            }
          })
          this.excelResolutionSelections = defaults
          this.excelPreviewModalOpen = false
          this.excelResolutionModalOpen = true
          this.notify('Importálás döntést kér', 'warning', 'AlertCircleIcon')
          return
        }
        const backendMessage = error?.response?.data?.message
        const backendOutput = error?.response?.data?.output
        this.importReport = error?.response?.data?.report || this.importReport
        this.importReportError = [backendMessage, backendOutput]
          .filter(part => part && String(part).trim() !== '')
          .join(' | ')
        if (this.importReport) this.importReportModalOpen = true
        this.notify(
          this.importReportError ? `Excel import sikertelen: ${this.importReportError}` : 'Excel import sikertelen',
          'danger',
          'XOctagonIcon',
        )
      }).finally(() => {
        this.importing = false
      })
    },
    confirmExcelResolution() {
      const resolution = this.excelImportResolution || {}
      const sheet = String(resolution.sheet || '')
      if (!sheet || !this.canSubmitResolution) return
      const existing = this.excelImportColumnOverrides[sheet] || {}
      const mapped = {}
      this.resolutionUnresolvedFields.forEach(field => {
        const selected = Number(this.excelResolutionSelections[field.field] || 0)
        if (selected > 0) {
          mapped[field.field] = selected
        }
      })
      this.$set(this.excelImportColumnOverrides, sheet, {
        ...existing,
        ...mapped,
      })
      this.excelResolutionModalOpen = false
      this.runExcelImport({ previewOnly: false })
    },
    previewTargetKey(target, idx) {
      return String(target?.sheet || target?.category || target?.menu || idx)
    },
    previewFieldOptions(target, field) {
      const columns = Array.isArray(target?.columns) ? target.columns : []
      const options = columns.map(col => ({
        value: Number(col.index),
        text: `${col.letter || ''} - ${col.label || ''}`,
      }))
      const unresolved = Array.isArray(target?.unresolved_fields) ? target.unresolved_fields : []
      const unresolvedField = unresolved.find(item => String(item?.field || '') === String(field))
      const candidates = Array.isArray(unresolvedField?.candidates) ? unresolvedField.candidates.map(v => Number(v)) : []
      if (!candidates.length) return options
      return options.filter(opt => candidates.includes(Number(opt.value)))
    },
    previewFieldLabel(field) {
      const labels = {
        ssz: 'Sorszám',
        item_text: 'Tétel szöveg',
        qty: 'Mennyiség',
        unit: 'Egység',
        material_unit_price: 'Anyag egységár',
        base_fee_unit: 'Díj egységár',
      }
      return labels[field] || field
    },
    importFieldOrder(field) {
      const order = {
        item_text: 1,
        qty: 2,
        unit: 3,
        material_unit_price: 4,
        base_fee_unit: 5,
      }
      return order[field] || 99
    },
    sortImportFields(fields) {
      return [...fields].sort((a, b) => {
        const left = this.importFieldOrder(String(a?.field || a || ''))
        const right = this.importFieldOrder(String(b?.field || b || ''))
        return left - right
      })
    },
    normalizeBudgetCategory(category) {
      return String(category || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/_/g, '-')
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
    },
    requiredFieldsForTarget(target) {
      const explicit = Array.isArray(target?.required_fields) ? target.required_fields.filter(Boolean) : []
      if (explicit.length) return this.sortImportFields(explicit)

      return ['item_text', 'qty', 'unit', 'material_unit_price', 'base_fee_unit']
    },
    guessColumnForField(target, field) {
      const columns = Array.isArray(target?.columns) ? target.columns : []
      if (!columns.length) return 0

      const normalize = value => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

      const keywordMap = {
        ssz: ['ssz', 'sorszam'],
        item_text: ['tetel', 'megnevezes', 'munkanem'],
        qty: ['menny', 'mennyiseg'],
        unit: ['egyseg', 'egys'],
        material_unit_price: ['anyag', 'egysegar', 'egysegar'],
        base_fee_unit: ['dij', 'egysegre', 'egysegar', 'alap dij'],
      }

      const keywords = keywordMap[field] || []
      if (!keywords.length) return 0

      let best = 0
      let bestScore = -1

      columns.forEach(col => {
        const label = normalize(col?.label)
        if (!label) return
        let score = 0
        keywords.forEach(token => {
          if (label.includes(token)) score += token.length
        })
        if (field === 'item_text' && (label.includes('ossz') || label.includes('netto'))) {
          score -= 4
        }
        if ((field === 'material_unit_price' || field === 'base_fee_unit') && label.includes('osszesen')) {
          score -= 8
        }
        if (score > bestScore) {
          bestScore = score
          best = Number(col?.index || 0)
        }
      })

      return bestScore > 0 ? best : 0
    },
    previewResolvedRows(target) {
      const resolved = target?.resolved_fields || {}
      const columns = Array.isArray(target?.columns) ? target.columns : []
      const labels = {
        ssz: 'Sorszám',
        item_text: 'Tétel szöveg',
        qty: 'Mennyiség',
        unit: 'Egység',
        material_unit_price: 'Anyag egységár',
        base_fee_unit: 'Díj egységár',
        material_total: 'Anyag összesen',
        fee_total: 'Díj összesen',
      }
      return Object.keys(labels)
        .filter(key => Number.isFinite(Number(resolved[key])) && Number(resolved[key]) > 0)
        .map(key => {
          const colIndex = Number(resolved[key])
          const col = columns.find(c => Number(c?.index) === colIndex)
          const colText = col ? `${col.letter || ''} - ${col.label || ''}` : `#${colIndex}`
          return {
            label: labels[key],
            column: colText,
          }
        })
    },
    hydratePreviewSelections() {
      const selections = {}
      this.previewTargets.forEach((target, idx) => {
        const key = this.previewTargetKey(target, idx)
        const required = this.requiredFieldsForTarget(target)
        const resolved = target?.resolved_fields || {}
        const unresolved = Array.isArray(target?.unresolved_fields) ? target.unresolved_fields : []
        const rowSelections = {}
        required.forEach(field => {
          const resolvedValue = Number(resolved?.[field] || 0)
          if (Number.isFinite(resolvedValue) && resolvedValue > 0) {
            rowSelections[field] = resolvedValue
            return
          }

          const unresolvedField = unresolved.find(item => String(item?.field || '') === String(field))
          const candidates = Array.isArray(unresolvedField?.candidates) ? unresolvedField.candidates : []
          const firstCandidate = Number(candidates[0] || 0)
          if (Number.isFinite(firstCandidate) && firstCandidate > 0) {
            rowSelections[field] = firstCandidate
            return
          }

          const guessed = Number(this.guessColumnForField(target, field) || 0)
          if (Number.isFinite(guessed) && guessed > 0) {
            rowSelections[field] = guessed
          }
        })
        selections[key] = rowSelections
      })
      this.excelPreviewSelections = selections
    },
    confirmExcelPreview() {
      const nextOverrides = { ...(this.excelImportColumnOverrides || {}) }
      this.previewTargets.forEach((target, idx) => {
        const sheet = String(target?.sheet || '')
        if (!sheet) return
        const key = this.previewTargetKey(target, idx)
        const required = this.requiredFieldsForTarget(target)
        if (!required.length) return
        const selectedMap = this.excelPreviewSelections[key] || {}
        const mapped = {}
        required.forEach(field => {
          const selected = Number(selectedMap[field] || 0)
          if (selected > 0) mapped[field] = selected
        })
        nextOverrides[sheet] = {
          ...(nextOverrides[sheet] || {}),
          ...mapped,
        }
      })
      this.excelImportColumnOverrides = nextOverrides
      this.excelPreviewModalOpen = false
      this.runExcelImport({ previewOnly: false })
    },
    fieldOptions(field) {
      const all = this.resolutionFieldOptions
      const candidates = Array.isArray(field?.candidates) ? field.candidates.map(v => Number(v)) : []
      if (!candidates.length) return all
      return all.filter(opt => candidates.includes(Number(opt.value)))
    },
    openClearModal() {
      if (!this.canClearBudget) return
      this.clearModalOpen = true
    },
    resetClearDraft() {
      this.clearing = false
      this.clearEntries = true
      this.clearMenu = false
    },
    confirmClearOfferData() {
      if (!this.canClearBudget) return
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)
      if (!offerType || !offerId) return
      if (!this.clearEntries && !this.clearMenu) return

      this.clearing = true
      axios({
        method: 'post',
        url: 'budget/offer/clear',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_type: offerType,
          offer_id: offerId,
          clear_entries: this.clearEntries,
          clear_menu: this.clearMenu,
        },
      }).then(() => {
        this.notify('Kártya adatok törlése kész', 'success', 'CheckIcon')
        this.clearModalOpen = false
        if (this.clearMenu && this.$store) {
          this.$store.commit('app/SET_PREP_BUDGET_CUSTOM_MENUS', [])
          this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', '')
        }
        this.fetchItems(false)
      }).catch(error => {
        const message = error?.response?.data?.message
        this.notify(message || 'Kártya adatok törlése sikertelen', 'danger', 'XOctagonIcon')
      }).finally(() => {
        this.clearing = false
      })
    },
    resolvedCategoryKey() {
      if (this.categoryKey) return this.categoryKey
      if (this.endpoint.includes('zsaluzas-allvanyozas')) return 'zsaluzas-allvanyozas'
      if (this.endpoint.includes('sikalapozas')) return 'sikalapozas'
      if (this.endpoint.includes('mely-alapozas')) return 'melyalapozas'
      if (this.endpoint.includes('/alapozas')) return 'alapozas'
      if (this.endpoint.includes('helyszini-beton-vasbeton')) return 'helyszini-beton-vasbeton'
      if (this.endpoint.includes('egyeb')) return 'egyeb'
      if (this.endpoint.includes('osszesito')) return 'osszesito'
      return ''
    },
    templateBindingKey() {
      return String(this.subgroupKey || this.categoryKey || this.resolvedCategoryKey() || '')
    },
    templateEntryKey() {
      return String(this.menuKey || this.templateBindingKey())
    },
    updateRow(change) {
      this.correctionBaseRows = null
      const row = this.rows.find(item => Number(item.id) === Number(change.item_id))
      if (!row) return
      row.qty = change.qty
      row.unit = change.unit
      row.material_unit_price = change.material_unit_price
      row.base_fee_unit = change.base_fee_unit
      row.planned_expense_scope = change.planned_expense_scope
      row.internal_note = change.internal_note || ''
      const qty = change.qty !== null && change.qty !== undefined && change.qty !== '' ? Number(change.qty) : null
      const mUnit = change.material_unit_price !== null && change.material_unit_price !== undefined && change.material_unit_price !== '' ? Number(change.material_unit_price) : null
      const dUnit = change.base_fee_unit !== null && change.base_fee_unit !== undefined && change.base_fee_unit !== '' ? Number(change.base_fee_unit) : null
      row.material_total = (qty !== null && mUnit !== null) ? Number((qty * mUnit).toFixed(2)) : null
      row.fee_total = (qty !== null && dUnit !== null) ? Number((qty * dUnit).toFixed(2)) : null
      this.hasChanges = true
      this.scheduleAutoPersist()
    },
    onRowsReordered(event) {
      if (!this.canReorderRows) return
      if (event && event.oldIndex === event.newIndex) return
      this.correctionBaseRows = null
      this.hasChanges = true
      this.scheduleAutoPersist()
    },
    scheduleAutoPersist() {
      if (this.isSummaryCategory) return
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer)
      }
      const entriesSnapshot = this.rows.map(row => ({
        item_id: row.id,
        qty: row.qty,
        unit: row.unit,
        material_unit_price: row.material_unit_price,
        base_fee_unit: row.base_fee_unit,
        planned_expense_scope: row.planned_expense_scope || 'none',
        internal_note: row.internal_note || '',
      }))
      const context = {
        category: this.resolvedCategoryKey(),
        offerType: String(this.offerType || ''),
        offerId: Number(this.offerId || 0),
      }
      this.pendingAutosaveContext = context
      this.pendingAutosaveEntries = entriesSnapshot
      this.autosaveTimer = setTimeout(() => {
        this.autosaveTimer = null
        this.persistEntriesSilent(this.pendingAutosaveContext, this.pendingAutosaveEntries)
        this.pendingAutosaveContext = null
        this.pendingAutosaveEntries = null
      }, 500)
    },
    flushAutoPersist() {
      if (!this.autosaveTimer) return
      clearTimeout(this.autosaveTimer)
      this.autosaveTimer = null
      this.persistEntriesSilent(this.pendingAutosaveContext, this.pendingAutosaveEntries)
      this.pendingAutosaveContext = null
      this.pendingAutosaveEntries = null
    },
    fetchItems(showToast = false) {
      this.loading = true
      this.error = ''
      this.rows = []
      this.correctionBaseRows = null

      const category = this.resolvedCategoryKey()
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)

      if (!category || !offerType || !offerId) {
        this.rows = []
        this.error = 'Hiányzó költségvetés azonosító.'
        this.loading = false
        return
      }

      if (this.usesTemplateBindings) {
        axios({
          method: 'get',
          url: 'budget/template-bindings/items',
          params: {
            main_menu_key: this.templateBindingKey(),
            sub_menu_key: '',
            entry_category: category,
            entry_menu_key: this.templateEntryKey(),
            offer_type: offerType,
            offer_id: offerId,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }).then(response => {
          const payload = response.data || {}
          const items = Array.isArray(payload.rows) ? payload.rows : []
          this.rows = items.map(item => ({
            id: item.binding_id || item.id,
            itemText: item.item_text || '',
            qty: item.qty ?? null,
            unit: item.unit || '',
            material_unit_price: item.material_unit_price ?? null,
            base_fee_unit: item.base_fee_unit ?? null,
            material_total: item.material_total ?? null,
            fee_total: item.fee_total ?? null,
            planned_expense_scope: item.planned_expense_scope || 'none',
            internal_note: item.internal_note || '',
            source_category: item.source_category || '',
            source_item_id: item.source_item_id || null,
          }))
          this.hasPersistedEntries = !!payload.has_entries
          this.hasChanges = !this.hasPersistedEntries
          if (showToast) this.notify('Frissítés sikeres', 'success', 'CheckIcon')
        }).catch(() => {
          this.error = 'Nem sikerült betölteni a kötött tételeket.'
          this.rows = []
          if (showToast) this.notify('Frissítés sikertelen', 'danger', 'XOctagonIcon')
        }).finally(() => {
          this.loading = false
        })
        return
      }

      axios({
        method: 'get',
        url: 'budget/offer/entries',
        params: {
          offer_type: offerType,
          offer_id: offerId,
          category,
          subgroup_key: this.subgroupKey || '',
          root_only_items: this.rootOnlyItems ? 1 : 0,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const payload = response.data || {}
        const items = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload.rows) ? payload.rows : [])
        const hasEntries = Array.isArray(payload)
          ? false
          : !!payload.has_entries
        this.rows = items.map(item => ({
          id: item.id,
          itemText: item.item_text || '',
          qty: item.qty ?? null,
          unit: item.unit || '',
          material_unit_price: item.material_unit_price ?? null,
          base_fee_unit: item.base_fee_unit ?? null,
          material_total: item.material_total ?? null,
          fee_total: item.fee_total ?? null,
          planned_expense_scope: item.planned_expense_scope || 'none',
          internal_note: item.internal_note || '',
        }))
        this.hasPersistedEntries = hasEntries
        this.hasChanges = !hasEntries
        if (showToast) {
          this.notify('Frissítés sikeres', 'success', 'CheckIcon')
        }
      }).catch(() => {
        this.error = 'Nem sikerült betölteni a tételeket.'
        this.rows = []
        if (showToast) {
          this.notify('Frissítés sikertelen', 'danger', 'XOctagonIcon')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    saveItems() {
      if (!this.canPersistEntries) return
      if (this.hasPersistedEntries) return
      this.persistEntries('Mentés sikeres', 'Mentés sikertelen')
    },
    refreshItems() {
      if (!this.canPersistEntries) return
      if (!this.hasPersistedEntries) return
      this.persistEntries('Frissítés sikeres', 'Frissítés sikertelen')
    },
    persistEntries(successTitle, errorTitle) {
      this.correctionBaseRows = null
      const category = this.resolvedCategoryKey()
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)
      if (!category || !offerType || !offerId) return

      this.saving = true
      if (this.usesTemplateBindings) {
        axios({
          method: 'post',
          url: 'budget/template-bindings/save-entries',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            offer_type: offerType,
            offer_id: offerId,
            main_menu_key: this.templateBindingKey(),
            sub_menu_key: '',
            entry_category: category,
            entry_menu_key: this.templateEntryKey(),
            entries: this.rows.map(row => ({
              source_category: row.source_category,
              source_item_id: row.source_item_id,
              qty: row.qty,
              unit: row.unit,
              material_unit_price: row.material_unit_price,
              base_fee_unit: row.base_fee_unit,
              planned_expense_scope: row.planned_expense_scope || 'none',
              internal_note: row.internal_note || '',
            })),
          },
        }).then(() => {
          this.hasPersistedEntries = true
          this.hasChanges = false
          this.notify(successTitle, 'success', 'CheckIcon')
          this.fetchItems(false)
        }).catch(() => {
          this.notify(errorTitle, 'danger', 'XOctagonIcon')
        }).finally(() => {
          this.saving = false
        })
        return
      }

      axios({
        method: 'post',
        url: 'budget/offer/entries',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_type: offerType,
          offer_id: offerId,
          category,
          subgroup_key: this.subgroupKey || '',
          entries: this.rows.map(row => ({
            item_id: row.id,
            qty: row.qty,
            unit: row.unit,
            material_unit_price: row.material_unit_price,
            base_fee_unit: row.base_fee_unit,
            planned_expense_scope: row.planned_expense_scope || 'none',
            internal_note: row.internal_note || '',
          })),
        },
      }).then(() => {
        this.hasPersistedEntries = true
        this.hasChanges = false
        this.notify(successTitle, 'success', 'CheckIcon')
        this.fetchItems(false)
      }).catch(() => {
        this.notify(errorTitle, 'danger', 'XOctagonIcon')
      }).finally(() => {
        this.saving = false
      })
    },
    persistEntriesSilent(context, entriesSnapshot = null) {
      this.correctionBaseRows = null
      const category = context?.category || this.resolvedCategoryKey()
      const offerType = context?.offerType || String(this.offerType || '')
      const offerId = context?.offerId || Number(this.offerId || 0)
      if (!category || !offerType || !offerId) return
      if (this.loading || this.saving || this.autosaving || !this.hasChanges) return
      if (!this.canPersistEntries) return
      const entries = Array.isArray(entriesSnapshot)
        ? entriesSnapshot
        : this.rows.map(row => ({
          item_id: row.id,
          qty: row.qty,
          unit: row.unit,
          material_unit_price: row.material_unit_price,
          base_fee_unit: row.base_fee_unit,
          planned_expense_scope: row.planned_expense_scope || 'none',
          internal_note: row.internal_note || '',
        }))

      this.autosaving = true
      if (this.usesTemplateBindings) {
        axios({
          method: 'post',
          url: 'budget/template-bindings/save-entries',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: {
            offer_type: offerType,
            offer_id: offerId,
            main_menu_key: this.templateBindingKey(),
            sub_menu_key: '',
            entry_category: category,
            entry_menu_key: this.templateEntryKey(),
            entries: this.rows.map(row => ({
              source_category: row.source_category,
              source_item_id: row.source_item_id,
              qty: row.qty,
              unit: row.unit,
              material_unit_price: row.material_unit_price,
              base_fee_unit: row.base_fee_unit,
              planned_expense_scope: row.planned_expense_scope || 'none',
              internal_note: row.internal_note || '',
            })),
          },
        }).then(() => {
          this.hasPersistedEntries = true
          this.hasChanges = false
        }).catch(() => {
          // csendes autosave hiba, kézi Mentés/Frissítés továbbra is elérhető
        }).finally(() => {
          this.autosaving = false
        })
        return
      }

      axios({
        method: 'post',
        url: 'budget/offer/entries',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_type: offerType,
          offer_id: offerId,
          category,
          subgroup_key: this.subgroupKey || '',
          entries,
        },
      }).then(() => {
        this.hasPersistedEntries = true
        this.hasChanges = false
      }).catch(() => {
        // csendes autosave hiba, kézi Mentés/Frissítés továbbra is elérhető
      }).finally(() => {
        this.autosaving = false
      })
    },
    copyRows(rows) {
      return (rows || []).map(row => ({ ...row }))
    },
    applyNegotiatedAdjustment(rows, correction) {
      const enabled = !!(correction?.negotiatedEnabled ?? correction?.negotiated_enabled)
      if (!enabled) return rows
      const targetNet = Math.round(Number(correction?.negotiatedNet ?? correction?.negotiated_net ?? 0))
      const applyMaterial = !!(correction?.negotiatedMaterial ?? correction?.negotiated_material)
      const applyFee = !!(correction?.negotiatedFee ?? correction?.negotiated_fee)
      if (!Number.isFinite(targetNet) || (!applyMaterial && !applyFee)) return rows

      const slots = []
      rows.forEach((row, idx) => {
        const qty = row.qty !== null && row.qty !== undefined && row.qty !== '' ? Number(row.qty) : null
        if (!Number.isFinite(qty) || qty === 0) return
        if (applyMaterial && row.material_unit_price !== null && row.material_unit_price !== undefined && row.material_unit_price !== '') {
          slots.push({ idx, type: 'material' })
        }
        if (applyFee && row.base_fee_unit !== null && row.base_fee_unit !== undefined && row.base_fee_unit !== '') {
          slots.push({ idx, type: 'fee' })
        }
      })

      if (!slots.length) return rows

      const currentNet = rows.reduce(
        (sum, row) => sum + (Number(row.material_total) || 0) + (Number(row.fee_total) || 0),
        0,
      )
      const delta = Number((targetNet - currentNet).toFixed(2))
      if (Math.abs(delta) < 0.01) return rows

      const nextRows = this.copyRows(rows)
      const getSlotTotal = slot => {
        const row = nextRows[slot.idx]
        return Number(slot.type === 'material' ? (row.material_total || 0) : (row.fee_total || 0))
      }
      const setSlotTotal = (slot, rawTotal) => {
        const row = nextRows[slot.idx]
        const qty = Number(row.qty)
        if (!Number.isFinite(qty) || qty === 0) return false
        const nextTotal = Number(Math.max(0, rawTotal).toFixed(2))
        const nextUnit = Number((nextTotal / qty).toFixed(2))
        if (slot.type === 'material') {
          row.material_total = nextTotal
          row.material_unit_price = nextUnit
        } else {
          row.fee_total = nextTotal
          row.base_fee_unit = nextUnit
        }
        return true
      }

      if (delta > 0) {
        const share = Number((delta / slots.length).toFixed(2))
        slots.forEach(slot => {
          const current = getSlotTotal(slot)
          setSlotTotal(slot, current + share)
        })
      } else {
        let remaining = Number(Math.abs(delta).toFixed(2))
        let active = [...slots]
        while (remaining >= 0.01 && active.length) {
          const share = Number((remaining / active.length).toFixed(2))
          if (share <= 0) break
          const nextActive = []
          active.forEach(slot => {
            const current = getSlotTotal(slot)
            const reduceBy = Math.min(current, share)
            setSlotTotal(slot, current - reduceBy)
            remaining = Number((remaining - reduceBy).toFixed(2))
            if ((current - reduceBy) > 0.009) nextActive.push(slot)
          })
          active = nextActive
        }
      }

      const afterNet = nextRows.reduce(
        (sum, row) => sum + (Number(row.material_total) || 0) + (Number(row.fee_total) || 0),
        0,
      )
      const remainder = Number((targetNet - afterNet).toFixed(2))
      if (Math.abs(remainder) >= 0.01 && slots.length) {
        const first = slots[0]
        const current = getSlotTotal(first)
        const adjusted = current + remainder
        if (adjusted >= 0) {
          setSlotTotal(first, adjusted)
        }
      }

      return nextRows
    },
    applyCorrectionPreview(correction) {
      if (this.loading) return
      if (!this.correctionBaseRows) {
        this.correctionBaseRows = this.copyRows(this.rows)
      }
      const globalPercent = Number(
        correction && typeof correction === 'object'
          ? (correction.globalPercent ?? correction.global_percent ?? 0)
          : (correction || 0),
      )
      const materialPercent = Number(
        correction && typeof correction === 'object'
          ? (correction.materialPercent ?? correction.material_percent ?? 0)
          : 0,
      )
      const feePercent = Number(
        correction && typeof correction === 'object'
          ? (correction.feePercent ?? correction.fee_percent ?? 0)
          : 0,
      )
      const globalMultiplier = 1 + (globalPercent / 100)
      const materialMultiplier = globalMultiplier * (1 + (materialPercent / 100))
      const feeMultiplier = globalMultiplier * (1 + (feePercent / 100))
      const correctedRows = this.correctionBaseRows.map(row => {
        const qty = row.qty !== null && row.qty !== undefined && row.qty !== '' ? Number(row.qty) : null
        const materialUnit = row.material_unit_price !== null && row.material_unit_price !== undefined && row.material_unit_price !== ''
          ? Number(row.material_unit_price)
          : null
        const feeUnit = row.base_fee_unit !== null && row.base_fee_unit !== undefined && row.base_fee_unit !== ''
          ? Number(row.base_fee_unit)
          : null
        const nextMaterialUnit = materialUnit === null ? null : Number((materialUnit * materialMultiplier).toFixed(2))
        const nextFeeUnit = feeUnit === null ? null : Number((feeUnit * feeMultiplier).toFixed(2))
        return {
          ...row,
          material_unit_price: nextMaterialUnit,
          base_fee_unit: nextFeeUnit,
          material_total: (qty !== null && nextMaterialUnit !== null) ? Number((qty * nextMaterialUnit).toFixed(2)) : null,
          fee_total: (qty !== null && nextFeeUnit !== null) ? Number((qty * nextFeeUnit).toFixed(2)) : null,
        }
      })
      this.rows = this.applyNegotiatedAdjustment(correctedRows, correction)
    },
    resetCorrectionPreview() {
      this.fetchItems(false)
    },
  },
}
</script>

<style scoped>
.prep-budget-save {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  position: sticky;
  top: 0;
  z-index: 6;
  padding: 0.35rem 0.4rem;
  border-radius: 12px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--prep-surface-1) 72%, #0a1020 28%) 0%, transparent 100%);
  backdrop-filter: blur(3px);
}

.prep-action-btn {
  border-radius: 10px !important;
  border-width: 1px !important;
  transition: transform 0.15s ease, box-shadow 0.18s ease, filter 0.18s ease !important;
  backdrop-filter: blur(2px);
}

.prep-action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.prep-action-btn-fill,
.prep-action-btn-import,
.prep-action-btn-assign,
.prep-action-btn-new,
.prep-action-btn-refresh,
.prep-action-btn-save {
  color: #e2ecff !important;
  border-color: color-mix(in srgb, var(--prep-accent-base) 45%, #ffffff 10%) !important;
  background-image: linear-gradient(
    120deg,
    color-mix(in srgb, var(--prep-accent-strong) 42%, rgba(15, 23, 42, 0.9) 58%) 0%,
    color-mix(in srgb, var(--prep-accent-base) 40%, rgba(30, 41, 59, 0.95) 60%) 55%,
    color-mix(in srgb, var(--prep-accent-soft) 35%, rgba(30, 41, 59, 0.95) 65%) 100%
  ) !important;
  box-shadow: 0 6px 18px color-mix(in srgb, var(--prep-accent-base) 35%, transparent 65%);
}

.prep-action-btn-fill:hover:not(:disabled),
.prep-action-btn-import:hover:not(:disabled),
.prep-action-btn-assign:hover:not(:disabled),
.prep-action-btn-new:hover:not(:disabled),
.prep-action-btn-refresh:hover:not(:disabled),
.prep-action-btn-save:hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--prep-accent-base) 46%, transparent 54%);
}

.prep-action-btn-clear {
  color: #fff1f2 !important;
  border-color: color-mix(in srgb, var(--prep-danger-base) 42%, #ffffff 12%) !important;
  background-image: linear-gradient(
    118deg,
    color-mix(in srgb, var(--prep-danger-base) 40%, rgba(15, 23, 42, 0.9) 60%) 0%,
    color-mix(in srgb, var(--prep-warning-base) 24%, rgba(30, 41, 59, 0.95) 76%) 100%
  ) !important;
  box-shadow: 0 6px 18px color-mix(in srgb, var(--prep-danger-base) 32%, transparent 68%);
}

.prep-action-btn-clear:hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--prep-danger-base) 44%, transparent 56%);
}

.prep-action-btn:disabled {
  opacity: 0.45 !important;
  box-shadow: none !important;
}

.prep-opcios-summary-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-left: 10px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--prep-text, #e5e7eb);
  user-select: none;

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: var(--prep-primary, #60a5fa);
  }
}

.prep-assign-list {
  max-height: 380px;
  overflow: auto;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  padding: 0.4rem;
  outline: none;
}

.prep-assign-item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.72);
  color: #e5e7eb;
  padding: 0.45rem 0.55rem;
  margin-bottom: 0.35rem;
  cursor: pointer;
}

.prep-assign-item.selected {
  border-color: #60a5fa;
  background: rgba(59, 130, 246, 0.2);
}

.prep-budget-summary-bar {
  position: fixed;
  left: 270px;
  right: 24px;
  bottom: 0;
  z-index: 50;
  min-height: 78px;
  border-radius: 12px 12px 0 0;
  background:
    radial-gradient(130% 140% at 4% 0%, color-mix(in srgb, var(--prep-accent-soft) 18%, transparent 82%) 0%, transparent 55%),
    linear-gradient(140deg, color-mix(in srgb, var(--prep-surface-1) 80%, #0b1220 20%) 0%, color-mix(in srgb, var(--prep-surface-2) 80%, #08101f 20%) 100%);
  border: 1px solid color-mix(in srgb, var(--prep-accent-soft) 26%, rgba(148, 163, 184, 0.28) 74%);
  box-shadow: 0 -14px 30px rgba(15, 23, 42, 0.42);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2rem;
  padding: 1.1rem 1.2rem;
  color: #e5e7eb;
}

.prep-budget-table-scroll {
  padding-bottom: 122px;
}

.prep-budget-item-list ::v-deep(.prep-budget-item-drag-chosen .prep-budget-item-card) {
  border-color: color-mix(in srgb, var(--prep-accent-base) 55%, var(--prep-card-border) 45%);
}

.prep-budget-item-list ::v-deep(.prep-budget-item-drag-ghost) {
  opacity: 0.45;
}

.prep-budget-item-list ::v-deep(.prep-budget-item-dragging .prep-budget-item-card) {
  transform: rotate(0.5deg);
  box-shadow: 0 20px 34px rgba(15, 23, 42, 0.36);
}

.prep-budget-summary-title {
  font-weight: 700;
  margin-right: 0.75rem;
  white-space: nowrap;
  font-size: 1.3rem;
  line-height: 1.1;
  color: #e5e7eb;
}

.prep-budget-summary-items {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2rem;
}

.prep-budget-summary-item {
  min-width: 247px;
  border: 1px solid color-mix(in srgb, var(--prep-warning-base) 66%, #fde68a 34%);
  border-radius: 8px;
  background:
    linear-gradient(135deg,
      color-mix(in srgb, var(--prep-warning-base) 80%, #fde047 20%) 0%,
      color-mix(in srgb, var(--prep-accent-soft) 24%, var(--prep-warning-base) 76%) 100%
    );
  padding: 0.45rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
}

.prep-budget-summary-item-label-box {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 6px;
  background:
    linear-gradient(120deg,
      color-mix(in srgb, var(--prep-surface-1) 84%, #111 16%) 0%,
      color-mix(in srgb, var(--prep-surface-2) 88%, #111 12%) 100%);
  padding: 0.28rem 0.45rem;
}

.prep-budget-summary-item-label {
  display: block;
  font-size: 0.95rem;
  color: #e5e7eb;
  font-weight: 700;
  white-space: normal;
}

.prep-budget-summary-item-value-box {
  border: 1px solid color-mix(in srgb, var(--prep-danger-base) 35%, rgba(148, 163, 184, 0.35) 65%);
  border-radius: 6px;
  background:
    linear-gradient(140deg,
      color-mix(in srgb, var(--prep-surface-2) 76%, #111 24%) 0%,
      color-mix(in srgb, var(--prep-danger-base) 12%, #111 88%) 100%);
  padding: 0.3rem 0.45rem;
}

.prep-budget-summary-item-value {
  display: block;
  color: color-mix(in srgb, var(--prep-danger-base) 80%, #fecaca 20%);
  line-height: 1.1;
  white-space: nowrap;
}

.prep-budget-summary-item.total {
  border-color: color-mix(in srgb, var(--prep-accent-base) 48%, var(--prep-warning-base) 52%);
  background:
    linear-gradient(130deg,
      color-mix(in srgb, var(--prep-warning-base) 64%, #fde047 36%) 0%,
      color-mix(in srgb, var(--prep-accent-base) 36%, var(--prep-warning-base) 64%) 100%);
}

@media (max-width: 1199px) {
  .prep-budget-summary-bar {
    left: 14px;
    right: 14px;
    bottom: 0;
    gap: 1rem 2rem;
    min-height: 78px;
    padding: 0.95rem;
    border-radius: 12px 12px 0 0;
  }

  .prep-budget-summary-item {
    width: 100%;
    min-width: 0;
  }

  .prep-budget-summary-items {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .prep-budget-summary-item-label {
    white-space: nowrap;
  }
}
</style>
